/**
 * Inventory Management Page
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

function Inventory() {
    const { id: brandId } = useParams();
    const { getAccessTokenSilently } = useAuth0();

    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [brandName, setBrandName] = useState('');

    // Modal state for adding category
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [newCategory, setNewCategory] = useState({ name: '', description: '' });

    // State for editing items
    const [editingCategory, setEditingCategory] = useState(null);
    const [itemForm, setItemForm] = useState(null); // If not null, we are adding/editing an item

    useEffect(() => {
        loadData();
    }, [brandId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const headers = { Authorization: `Bearer ${token}` };

            const [brandRes, invRes] = await Promise.all([
                axios.get(`/api/admin/brands/${brandId}`, { headers }),
                axios.get(`/api/admin/brands/${brandId}/inventory`, { headers })
            ]);

            setBrandName(brandRes.data.brand.name);
            setInventory(invRes.data.inventory || []);

        } catch (err) {
            console.error('Error loading inventory:', err);
            alert('Failed to load inventory data');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        try {
            const token = await getAccessTokenSilently();
            await axios.post(`/api/admin/brands/${brandId}/inventory`, {
                categoryName: newCategory.name,
                categoryDescription: newCategory.description
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setShowAddCategory(false);
            setNewCategory({ name: '', description: '' });
            loadData();
        } catch (err) {
            console.error('Error creating category:', err);
            alert('Failed to create category');
        }
    };

    const handleDeleteCategory = async (invId) => {
        if (!window.confirm('Are you sure? This will delete the category and all items within it.')) return;
        try {
            const token = await getAccessTokenSilently();
            await axios.delete(`/api/admin/inventory/${invId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            loadData();
        } catch (err) {
            console.error('Error deleting category:', err);
            alert('Failed to delete category');
        }
    };

    const handleSaveItem = async (e) => {
        e.preventDefault();
        if (!editingCategory) return;

        const updatedItems = [...editingCategory.items];

        if (itemForm.index !== undefined) {
            // Edit existing
            updatedItems[itemForm.index] = { ...itemForm.data };
        } else {
            // Add new
            updatedItems.push({ ...itemForm.data });
        }

        try {
            const token = await getAccessTokenSilently();
            // Update the whole category inventory document
            await axios.put(`/api/admin/inventory/${editingCategory._id}`, {
                items: updatedItems
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update local state to reflect change immediately or reload
            setEditingCategory(prev => ({ ...prev, items: updatedItems }));
            setItemForm(null); // Close item form

            // Also update main list
            setInventory(prev => prev.map(inv => inv._id === editingCategory._id ? { ...inv, items: updatedItems } : inv));

        } catch (err) {
            console.error('Error saving item:', err);
            alert('Failed to save item');
        }
    };

    const handleDeleteItem = async (index) => {
        if (!window.confirm('Remove this item?')) return;
        const updatedItems = editingCategory.items.filter((_, i) => i !== index);

        try {
            const token = await getAccessTokenSilently();
            await axios.put(`/api/admin/inventory/${editingCategory._id}`, {
                items: updatedItems
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setEditingCategory(prev => ({ ...prev, items: updatedItems }));
            setInventory(prev => prev.map(inv => inv._id === editingCategory._id ? { ...inv, items: updatedItems } : inv));

        } catch (err) {
            console.error('Error deleting item:', err);
            alert('Failed to delete item');
        }
    };

    if (loading) return <div className="p-5 text-center">Loading...</div>;

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-0">
                        <Link to={`/admin/brands/${brandId}`} className="text-decoration-none text-muted me-2">{brandName} /</Link>
                        Inventory
                    </h2>
                    <p className="text-muted">Manage products, services, and knowledge base resources</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAddCategory(true)}>
                    <i className="bi bi-plus-lg me-2"></i>
                    Add Category
                </button>
            </div>

            {showAddCategory && (
                <div className="card mb-4 bg-light">
                    <div className="card-body">
                        <h5>New Category</h5>
                        <form onSubmit={handleCreateCategory} className="row g-3">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Category Name (e.g. Menu, Services)"
                                    value={newCategory.name}
                                    onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Description"
                                    value={newCategory.description}
                                    onChange={e => setNewCategory({ ...newCategory, description: e.target.value })}
                                />
                            </div>
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-success w-100">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="row g-4">
                {inventory.map(cat => (
                    <div key={cat._id} className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-0">{cat.categoryName}</h5>
                                    {cat.categoryDescription && <small className="text-muted">{cat.categoryDescription}</small>}
                                </div>
                                <div>
                                    <button
                                        className="btn btn-sm btn-outline-primary me-2"
                                        onClick={() => setEditingCategory(editingCategory?._id === cat._id ? null : cat)}
                                    >
                                        {editingCategory?._id === cat._id ? 'Done Editing' : 'Edit Items'}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDeleteCategory(cat._id)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="card-body">
                                {cat.items.length === 0 ? (
                                    <p className="text-muted mb-0">No items.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Description</th>
                                                    <th>Price</th>
                                                    <th>Stock</th>
                                                    {editingCategory?._id === cat._id && <th>Actions</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {cat.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.name}</td>
                                                        <td>{item.description}</td>
                                                        <td>{item.price?.display || '-'}</td>
                                                        <td>
                                                            {item.inStock ?
                                                                <span className="badge bg-success">In Stock</span> :
                                                                <span className="badge bg-secondary">Out of Stock</span>
                                                            }
                                                        </td>
                                                        {editingCategory?._id === cat._id && (
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-link p-0 me-2"
                                                                    onClick={() => setItemForm({ index: idx, data: item })}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-link text-danger p-0"
                                                                    onClick={() => handleDeleteItem(idx)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Add/Edit Item Form inline */}
                                {editingCategory?._id === cat._id && (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <h6>{itemForm ? 'Edit Item' : 'Add New Item'}</h6>
                                        <form onSubmit={handleSaveItem}>
                                            <div className="row g-2">
                                                <div className="col-md-3">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Name"
                                                        value={itemForm ? itemForm.data.name : ''}
                                                        onChange={e => setItemForm(prev => ({
                                                            ...prev,
                                                            data: { ...prev?.data, name: e.target.value }
                                                        }) || { index: undefined, data: { name: e.target.value, inStock: true } })}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Description"
                                                        value={itemForm ? itemForm.data.description || '' : ''}
                                                        onChange={e => setItemForm(prev => ({
                                                            ...prev,
                                                            data: { ...prev?.data, description: e.target.value }
                                                        }))}
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Price ($)"
                                                        value={itemForm ? itemForm.data.price?.display || '' : ''}
                                                        onChange={e => setItemForm(prev => ({
                                                            ...prev,
                                                            data: { ...prev?.data, price: { ...prev?.data.price, display: e.target.value } }
                                                        }))}
                                                    />
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-check pt-1">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={itemForm ? itemForm.data.inStock : true}
                                                            onChange={e => setItemForm(prev => ({
                                                                ...prev,
                                                                data: { ...prev?.data, inStock: e.target.checked }
                                                            }))}
                                                        />
                                                        <label className="form-check-label small">In Stock</label>
                                                    </div>
                                                </div>
                                                <div className="col-md-1">
                                                    <button type="submit" className="btn btn-sm btn-primary w-100">Save</button>
                                                </div>
                                            </div>
                                            {itemForm && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-muted mt-1"
                                                    onClick={() => setItemForm(null)}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </form>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default Inventory;
